// GAVION ai-email (spec §6 Demo/FakeRest): the AiApi of the demo build. Deterministic text from the fake drafter, so
// the whole flow (on demand, proposed drafts, approval) works with no API key. Mirrors the SQL rules: one pending
// proposed draft per lead (a new one supersedes), owner-or-admin rejection, on-demand rows never listed.
import type { RecordStore } from "../../dialer/api/recordStore";
import { fakeDraft } from "../../../../../supabase/functions/_shared/ai/fakeDrafter.ts";
import type { TimelineRow } from "../../../../../supabase/functions/_shared/ai/types.ts";
import { callBriefSettingsOf } from "../../../../../supabase/functions/_shared/ai/callBrief.ts"; // GAVION call-brief-pipeline
import { fakeCallBrief } from "../brief/fakeCallBrief"; // GAVION call-brief-pipeline
import { buildFakeContext } from "./fakeAiContext";
import {
  demoCancelPlan,
  demoMoveTouch,
  demoStartPlan,
  demoWriteNow,
  fakeTouchDecided,
  type FakeFollowUpDeps,
} from "./fakeFollowUp";
import { AiApiError, callBriefRowOf, type AiApi } from "./types";

type Row = Record<string, unknown> & { id: number };
const DAY = 86_400_000;
const same = (a: unknown, b: unknown) => String(a) === String(b);

export function createFakeAiApi(o: {
  store: RecordStore;
  me: number;
  isAdmin: boolean;
  timeline: (companyId: number) => Promise<TimelineRow[]>;
  now?: () => Date;
}): AiApi {
  const now = o.now ?? (() => new Date());
  const drafts = () => o.store.list<Row>("ai_email_drafts");
  const fu = (): FakeFollowUpDeps => ({
    store: o.store,
    me: o.me,
    isAdmin: o.isAdmin,
    now,
    timeline: o.timeline,
  });
  return {
    async draft(i) {
      const instruction = i.instruction.trim() || null;
      const input = await buildFakeContext(o.store, o.timeline, {
        companyId: i.companyId,
        contactId: i.contactId,
        dealId: i.dealId,
        salesId: o.me,
        trigger: "on_demand",
        instruction,
        clientMessage: null,
        recipientEmail: null,
        sourceType: null,
        sourceId: null,
      });
      if (!input) throw new AiApiError("lead_not_found");
      const out = fakeDraft(input, i.variant);
      const row = await o.store.create<Row>("ai_email_drafts", {
        company_id: i.companyId,
        contact_id: i.contactId,
        deal_id: i.dealId,
        sales_id: o.me,
        trigger: "on_demand",
        source_type: null,
        source_id: null,
        to_email: input.recipient.email,
        ...out,
        instruction,
        status: "pending",
        edited: false,
        model: "claude-haiku-5-5",
        created_at: now().toISOString(),
        expires_at: new Date(now().getTime() + DAY).toISOString(),
      });
      return { draftId: row.id, ...out };
    },
    async rejectDraft(id, reason) {
      const d = await o.store.get<Row>("ai_email_drafts", id);
      if (!d) throw new AiApiError("not_found");
      if (Number(d.sales_id) !== o.me && !o.isAdmin)
        throw new AiApiError("forbidden");
      if (d.status !== "pending") throw new AiApiError("not_pending");
      await o.store.update("ai_email_drafts", id, {
        status: "rejected",
        reject_reason: reason,
        decided_at: now().toISOString(),
        decided_by: o.me,
      });
      await fakeTouchDecided(o.store, id, "rejected"); // GAVION ai follow-up
    },
    async rebuildStyle() {
      return { status: "too_few", sourceCount: 0 }; // no Outlook in the demo: the default style applies
    },
    async saveStyle(profileText) {
      const text = profileText.trim();
      if (!text || text.length > 4000) throw new AiApiError("invalid_profile");
      const mine = (await o.store.list<Row>("ai_style_profiles")).find(
        (s) => Number(s.sales_id) === o.me,
      );
      const patch = {
        sales_id: o.me,
        profile_text: text,
        edited_at: now().toISOString(),
      };
      if (mine) await o.store.update("ai_style_profiles", mine.id, patch);
      else
        await o.store.create("ai_style_profiles", {
          excerpts: [],
          source_count: 0,
          built_at: null,
          ...patch,
        });
    },
    // GAVION ai follow-up (spec §6): the FakeRest twin (fakeFollowUp.ts)
    cancelFollowUp: (planId) => demoCancelPlan(fu(), planId),
    moveFollowUpTouch: (touchId, day) => demoMoveTouch(fu(), touchId, day),
    writeFollowUpTouchNow: (touchId) => demoWriteNow(fu(), touchId),
    demoFollowUp: (i) => demoStartPlan(fu(), i),
    async demoAfterCall({ companyId, contactId, callId, outcome }) {
      const input = await buildFakeContext(
        o.store,
        o.timeline,
        {
          companyId,
          contactId,
          dealId: null,
          salesId: o.me,
          trigger: "after_call",
          instruction: null,
          clientMessage: null,
          recipientEmail: null,
          sourceType: "call",
          sourceId: callId,
        },
        { id: callId, outcome, email: null },
      );
      if (!input || !input.recipient.email) return; // eligibility: no email → nothing is created
      for (const d of await drafts())
        if (
          same(d.company_id, companyId) &&
          d.status === "pending" &&
          d.trigger !== "on_demand"
        )
          await o.store.update("ai_email_drafts", d.id, {
            status: "superseded",
            decided_at: now().toISOString(),
          });
      await o.store.create("ai_email_drafts", {
        company_id: companyId,
        contact_id: input.recipient.contactId,
        deal_id: input.deal?.id ?? null,
        sales_id: o.me,
        trigger: "after_call",
        source_type: "call",
        source_id: callId,
        to_email: input.recipient.email,
        ...fakeDraft(input),
        status: "pending",
        edited: false,
        model: "claude-haiku-5-5",
        created_at: now().toISOString(),
        expires_at: new Date(now().getTime() + 7 * DAY).toISOString(),
      });
    },
    // GAVION call-brief-pipeline (spec A3): the demo's brief is written at once; the switch is honoured
    async requestCallBriefs(ids) {
      const cfg = await o.store.get<Row>("configuration", 1);
      if (!callBriefSettingsOf(cfg?.config).enabled)
        return { outcome: "disabled", capped: [] };
      const rows = await o.store.list<Row>("call_briefs");
      for (const id of ids) {
        const company = await o.store.get<Row>("companies", id);
        if (!company) continue;
        const brief = {
          company_id: id,
          ...fakeCallBrief(company),
          status: "ok",
          website_used: false,
          generated_at: now().toISOString(),
        };
        const existing = rows.find((r) => same(r.company_id, id));
        if (existing) await o.store.update("call_briefs", existing.id, brief);
        else await o.store.create("call_briefs", brief);
        // GAVION ops-lookup: the demo searches the web for nobody: « Non trouvé » at once
        if (!company.ops_contact_status)
          await o.store.update("companies", id, {
            ops_contact_status: "not_found",
            ops_contact_found_at: now().toISOString(),
          });
      }
      return { outcome: "ok", capped: [] };
    },
    async callBrief(companyId) {
      const row = (await o.store.list<Row>("call_briefs")).find((r) =>
        same(r.company_id, companyId),
      );
      return row ? callBriefRowOf(row) : null;
    },
  };
}
